/**
 * API Context for LeanAI Frontend
 */

import React, { createContext, useContext, ReactNode } from 'react';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAuth } from './AuthContext';

// Require explicit API URL configuration - fail fast if not set
const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable must be set');
}


interface ApiContextType {
  api: AxiosInstance;
  get: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) => Promise<T>;
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) => Promise<T>;
  delete: (url: string, config?: AxiosRequestConfig) => Promise<void>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const ApiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, loading } = useAuth();
  
  
  const api = React.useMemo(() => {
    const instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for adding auth token from AuthContext
    instance.interceptors.request.use(
      (config) => {
        // Block requests while auth is still loading to prevent race conditions
        if (loading) {
          return Promise.reject(new Error('Authentication still loading'));
        }
        
        // Add token from AuthContext if available
        if (token) {
          const authHeader = `Bearer ${token}`;
          config.headers.Authorization = authHeader;
        } else {
          return Promise.reject(new Error('No authentication token available'));
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for handling errors
    instance.interceptors.response.use(
      (response) => response.data,
      (error) => {
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, loading]);

  const get = async <T,>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    // Wait for auth to finish loading before making requests
    if (loading) {
      await new Promise(resolve => {
        const checkAuth = () => {
          if (!loading) {
            resolve(void 0);
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    }
    
    const response = await api.get<T>(url, config);
    return response as T;
  };

  const post = async <T,>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    // Wait for auth to finish loading before making requests
    if (loading) {
      await new Promise(resolve => {
        const checkAuth = () => {
          if (!loading) {
            resolve(void 0);
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    }
    
    const response = await api.post<T>(url, data, config);
    return response as T;
  };

  const put = async <T,>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    // Wait for auth to finish loading before making requests
    if (loading) {
      await new Promise(resolve => {
        const checkAuth = () => {
          if (!loading) {
            resolve(void 0);
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    }
    
    const response = await api.put<T>(url, data, config);
    return response as T;
  };

  const del = async (url: string, config?: AxiosRequestConfig): Promise<void> => {
    // Wait for auth to finish loading before making requests
    if (loading) {
      await new Promise(resolve => {
        const checkAuth = () => {
          if (!loading) {
            resolve(void 0);
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    }
    
    await api.delete(url, config);
  };

  const value = {
    api,
    get,
    post,
    put,
    delete: del,
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export default ApiContext;
